import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { isValidIndianMobile } from "../../api/suboperators";
import {
  STAFF_ROLES,
  staffRolesFor,
  actionsFor,
  canReissue,
  inviteSMSNote,
  inviteStaff,
  listOperatorLogins,
  reissueOperatorLogin,
  roleLabels,
  setOperatorLoginRoles,
  setOperatorLoginStatus,
  statusLabel,
  type InviteInput,
  type LoginAction,
  type OperatorLogin,
  type StaffRole,
} from "../../api/operatorLogins";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function inviteProblems(i: InviteInput, roles: readonly StaffRole[] | null): string[] {
  const p: string[] = [];
  if (!i.firstname.trim() || !i.lastname.trim()) p.push("Enter the first and last name.");
  if (!isValidIndianMobile(i.mobile)) p.push("Enter a 10-digit Indian mobile number.");
  if (roles !== null && roles.length === 0) p.push("Choose at least one role.");
  return p;
}

export function RoleChecks({ value, onChange, options = STAFF_ROLES }: { value: StaffRole[]; onChange: (v: StaffRole[]) => void; options?: typeof STAFF_ROLES }) {
  return (
    <FormGroup>
      {options.map((r) => (
        <FormControlLabel
          key={r.role}
          control={
            <Checkbox
              checked={value.includes(r.role)}
              onChange={(e) => onChange(e.target.checked ? [...value, r.role] : value.filter((x) => x !== r.role))}
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.label}</Typography>
              <Typography variant="caption" color="text.secondary">{r.help}</Typography>
            </Box>
          }
        />
      ))}
    </FormGroup>
  );
}

/**
 * The invitation form, for staff (with roles) and for a sub-operator's owner
 * (roles === null: the owner always gets `operator`).
 */
export function InviteDialog({
  open,
  title,
  intro,
  withRoles,
  roleOptions,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  intro: string;
  withRoles: boolean;
  /** The roles offered (io.ankhos offers its platform roles). */
  roleOptions?: typeof STAFF_ROLES | undefined;
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (input: InviteInput, roles: StaffRole[]) => void;
}) {
  const [form, setForm] = useState<InviteInput>({ firstname: "", lastname: "", mobile: "" });
  const [roles, setRoles] = useState<StaffRole[]>([]);
  const [shown, setShown] = useState<string[]>([]);
  const submit = () => {
    const p = inviteProblems(form, withRoles ? roles : null);
    setShown(p);
    if (p.length === 0) onSubmit(form, roles);
  };
  return (
    <Dialog open={open} onClose={() => !pending && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">{intro}</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField label="First name" value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })} fullWidth required />
          <TextField label="Last name" value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })} fullWidth required />
        </Stack>
        <TextField label="Mobile number" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} helperText="Their own mobile. It becomes their sign-in." required />
        {withRoles && <RoleChecks value={roles} onChange={setRoles} options={roleOptions ?? STAFF_ROLES} />}
        {shown.length > 0 && (
          <Alert severity="warning">
            {shown.map((p) => (
              <div key={p}>{p}</div>
            ))}
          </Alert>
        )}
        {Boolean(error) && <Alert severity="error">{errText(error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={pending}>Send invitation</Button>
      </DialogActions>
    </Dialog>
  );
}

export function statusChip(l: OperatorLogin) {
  const color = l.status === "ACTIVE" ? "success" : l.status === "PENDING" ? "info" : l.status === "DISABLED" ? "warning" : "default";
  return <Chip size="small" variant="outlined" color={color} label={statusLabel(l.status)} />;
}

const ACTION_LABEL: Record<LoginAction, string> = { cancel: "Cancel invitation", disable: "Disable", enable: "Enable" };

/**
 * Settings › Staff (owner, 2026-10-18): the people who sign in for this
 * operator. The owner invites each by name and mobile with one or more roles;
 * they accept at /invite and choose their own password. Disabling stops the
 * login signing in and keeps it on record -- nothing is deleted.
 */
export function StaffScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const key = ["operatorlogins", ns, "staff"];
  const list = useQuery({
    queryKey: key,
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listOperatorLogins(identity, ns, "staff", signal),
    retry: false,
  });
  const [inviting, setInviting] = useState(false);
  const [notice, setNotice] = useState("");
  const invite = useMutation({
    mutationFn: (v: { input: InviteInput; roles: StaffRole[] }) => inviteStaff(identity, ns, v.input, v.roles),
    onSuccess: async (l) => {
      await qc.invalidateQueries({ queryKey: key });
      setInviting(false);
      setNotice(`Invitation recorded for ${l.firstname} ${l.lastname}. ${inviteSMSNote(l)} They open ankhos.io/signin, choose "Accept your invitation" and verify ${l.mobile}. It expires in 14 days.`);
    },
  });
  const [act, setAct] = useState<{ login: OperatorLogin; action: LoginAction } | null>(null);
  const [note, setNote] = useState("");
  const status = useMutation({
    mutationFn: (v: { login: OperatorLogin; action: LoginAction }) => setOperatorLoginStatus(identity, ns, v.login.id, v.action, note),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: key });
      setAct(null);
      setNote("");
    },
  });
  const [editing, setEditing] = useState<{ login: OperatorLogin; roles: StaffRole[] } | null>(null);
  const roles = useMutation({
    mutationFn: (v: { login: OperatorLogin; roles: StaffRole[] }) => setOperatorLoginRoles(identity, ns, v.login.id, v.roles),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: key });
      setEditing(null);
    },
  });
  const [handing, setHanding] = useState<{ login: OperatorLogin; form: InviteInput; note: string; shown: string[] } | null>(null);
  const reissue = useMutation({
    mutationFn: (v: { login: OperatorLogin; form: InviteInput; note: string }) => reissueOperatorLogin(identity, ns, v.login.id, v.form, v.note),
    onSuccess: async (l, v) => {
      await qc.invalidateQueries({ queryKey: key });
      setHanding(null);
      const same = l.mobile === v.login.mobile;
      setNotice(`${same ? "Reset" : "Replaced"}: the login is switched off until ${l.firstname} ${l.lastname} opens ankhos.io/signin, chooses "Accept your invitation", verifies ${l.mobile} and sets a password. ${inviteSMSNote(l)} It expires in 14 days.`);
    },
  });
  const openHanding = (l: OperatorLogin) => {
    reissue.reset();
    setHanding({ login: l, form: { firstname: l.firstname, lastname: l.lastname, mobile: l.mobile.replace(/^\+91/, "") }, note: "", shown: [] });
  };
  const submitHanding = () => {
    if (!handing) return;
    const p = inviteProblems(handing.form, null);
    setHanding({ ...handing, shown: p });
    if (p.length === 0) reissue.mutate(handing);
  };
  const rows = list.data ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h5" component="h1">Staff</Typography>
          <Typography variant="body2" color="text.secondary">
            People who sign in for <code>{ns}</code>. Each signs in with their own mobile; you choose what they can do.
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" onClick={() => { invite.reset(); setInviting(true); }}>+ Invite staff</Button>
      </Box>
      {notice && <Alert severity="success" onClose={() => setNotice("")}>{notice}</Alert>}
      {list.error ? (
        <Alert severity="warning">Could not list your staff: {errText(list.error)}</Alert>
      ) : !list.isLoading && rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No staff yet. Invite the first person with + Invite staff.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((l) => (
                <TableRow key={l.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{l.firstname} {l.lastname}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{l.mobile}</TableCell>
                  <TableCell>{roleLabels(l.roles)}</TableCell>
                  <TableCell>{statusChip(l)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {["PENDING", "ACTIVE", "DISABLED"].includes(l.status) && (
                      <Button size="small" sx={{ mr: 1 }} onClick={() => { roles.reset(); setEditing({ login: l, roles: l.roles.filter((r): r is StaffRole => staffRolesFor(ns).some((x) => x.role === r)) }); }}>
                        Roles
                      </Button>
                    )}
                    {canReissue(l) && (
                      <Button size="small" sx={{ mr: 1 }} onClick={() => openHanding(l)}>
                        Reset or replace
                      </Button>
                    )}
                    {actionsFor(l).map((a) => (
                      <Button key={a} size="small" color={a === "enable" ? "primary" : "warning"} onClick={() => { status.reset(); setAct({ login: l, action: a }); }}>
                        {ACTION_LABEL[a]}
                      </Button>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {inviting && (
        <InviteDialog
          open
          title="Invite staff"
          intro={ns === "io.ankhos"
            ? "They get a platform login of their own at io.ankhos -- never inside an operator. We text them the sign-in link; they open ankhos.io/signin and choose Accept your invitation."
            : "They get a login of their own for this operator. We text them the sign-in link; they open ankhos.io/signin and choose Accept your invitation."}
          withRoles
          roleOptions={staffRolesFor(ns)}
          pending={invite.isPending}
          error={invite.error}
          onClose={() => setInviting(false)}
          onSubmit={(input, r) => invite.mutate({ input, roles: r })}
        />
      )}

      <Dialog open={act !== null} onClose={() => !status.isPending && setAct(null)} fullWidth maxWidth="sm">
        <DialogTitle>{act ? `${ACTION_LABEL[act.action]}: ${act.login.firstname} ${act.login.lastname}?` : ""}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2">
            {act?.action === "disable"
              ? "They can no longer sign in. The login and everything it did stay on record; you can enable it again."
              : act?.action === "enable"
                ? "They can sign in again with their mobile and password."
                : "The invitation can no longer be accepted. You can invite the same number again."}
          </Typography>
          {act?.action !== "enable" && <TextField label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 255 }} />}
          {status.error && <Alert severity="error">{errText(status.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAct(null)} disabled={status.isPending}>Back</Button>
          <Button variant="contained" color={act?.action === "enable" ? "primary" : "warning"} disabled={status.isPending} onClick={() => act && status.mutate(act)}>
            {act ? ACTION_LABEL[act.action] : ""}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={handing !== null} onClose={() => !reissue.isPending && setHanding(null)} fullWidth maxWidth="sm">
        <DialogTitle>{handing ? `Reset or replace: ${handing.login.firstname} ${handing.login.lastname}` : ""}</DialogTitle>
        {handing && (
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="body2">
              Keep the mobile to <b>reset</b> the login for the same person. Enter another person's name and mobile to <b>replace</b> them.
              The login, its roles and its history stay; only the person changes.
            </Typography>
            <Alert severity="warning">
              The login is switched off now. It works again only when the person opens ankhos.io/signin, chooses Accept your invitation,
              verifies the mobile with an SMS code and sets their own password. You never set or see the password.
            </Alert>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="First name" value={handing.form.firstname} onChange={(e) => setHanding({ ...handing, form: { ...handing.form, firstname: e.target.value } })} fullWidth required />
              <TextField label="Last name" value={handing.form.lastname} onChange={(e) => setHanding({ ...handing, form: { ...handing.form, lastname: e.target.value } })} fullWidth required />
            </Stack>
            <TextField label="Mobile number" value={handing.form.mobile} onChange={(e) => setHanding({ ...handing, form: { ...handing.form, mobile: e.target.value } })} helperText="The person's own mobile. It becomes their sign-in." required />
            <TextField label="Note (optional)" value={handing.note} onChange={(e) => setHanding({ ...handing, note: e.target.value })} inputProps={{ maxLength: 255 }} />
            {handing.shown.length > 0 && (
              <Alert severity="warning">
                {handing.shown.map((p) => (
                  <div key={p}>{p}</div>
                ))}
              </Alert>
            )}
            {reissue.error && <Alert severity="error">{errText(reissue.error)}</Alert>}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setHanding(null)} disabled={reissue.isPending}>Back</Button>
          <Button variant="contained" color="warning" disabled={reissue.isPending} onClick={submitHanding}>
            Switch off and re-invite
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editing !== null} onClose={() => !roles.isPending && setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? `Roles: ${editing.login.firstname} ${editing.login.lastname}` : ""}</DialogTitle>
        <DialogContent>
          {editing && <RoleChecks value={editing.roles} onChange={(v) => setEditing({ ...editing, roles: v })} options={staffRolesFor(ns)} />}
          {editing?.login.status === "ACTIVE" && (
            <Typography variant="caption" color="text.secondary">They see the change the next time they sign in.</Typography>
          )}
          {roles.error && <Alert severity="error" sx={{ mt: 1 }}>{errText(roles.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={roles.isPending}>Back</Button>
          <Button variant="contained" disabled={roles.isPending || !editing || editing.roles.length === 0} onClick={() => editing && roles.mutate(editing)}>
            Save roles
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
